import { useCallback } from 'react';

export type DynamicTextProp<T extends unknown[] = [unknown]> = string | ((...args: T) => string) | undefined;

/**
 * Hook that creates a resolver function for dynamic text props.
 * Handles text props that can be either a static string or a function that receives one or more arguments.
 *
 * @param textProp - The text prop value (string, function, or undefined)
 * @param defaultFn - A function that returns the default text given the arguments
 * @returns A resolver function that takes the arguments and returns the resolved string
 *
 * @example
 * ```tsx
 * // Single argument
 * const resolveGoToPage = useDynamicTextResolver(
 *   text?.goToPage,
 *   (page) => `Go to page ${page}`
 * );
 * <button aria-label={resolveGoToPage(5)}>5</button>
 *
 * // Multiple arguments
 * const resolvePageInfo = useDynamicTextResolver(
 *   text?.pageInfo,
 *   (start, end, total) => `Showing ${start}-${end} of ${total}`
 * );
 * <span>{resolvePageInfo(1, 10, 100)}</span>
 * ```
 */
function useDynamicTextResolver<T extends unknown[]>(
  textProp: DynamicTextProp<T>,
  defaultFn: (...args: T) => string,
): (...args: T) => string {
  return useCallback(
    (...args: T) => {
      if (typeof textProp === 'function') return textProp(...args);
      if (typeof textProp === 'string') return textProp;
      return defaultFn(...args);
    },
    [textProp],
  );
}

export { useDynamicTextResolver };
